import Dialog from "@components/Dialog"
import SubscriptionForm from "@components/SubscriptionForm"
import localizeCurrency from "@helpers/localizeCurrency"
import { useTranslation } from "react-i18next"
import { Plan, StripePrice } from "src/types"

interface Props {
  currency: string
  isSubmitting: boolean
  open: boolean
  onClose: () => void
  plan: Plan
  setIsSubmitting: (isSubmitting: boolean) => void
  stripeIntroPrice: StripePrice | undefined
  stripePrice: StripePrice | undefined
}

export default function LimitedTimeOfferDialog({
  currency,
  isSubmitting,
  onClose,
  open,
  plan,
  setIsSubmitting,
  stripeIntroPrice,
  stripePrice,
}: Props) {
  const { t } = useTranslation()

  if (!stripeIntroPrice || !stripePrice) return null

  const hasCurrencyOption =
    !!stripePrice.currency_options?.[currency]?.unit_amount_decimal

  const price = parseFloat(
    hasCurrencyOption
      ? stripePrice.currency_options[currency].unit_amount_decimal
      : stripePrice?.unit_amount_decimal,
  )

  const priceCurrency = hasCurrencyOption ? currency : stripePrice.currency

  if (!price) return null

  const introPrice = parseFloat(
    hasCurrencyOption
      ? stripeIntroPrice.currency_options[currency].unit_amount_decimal
      : stripeIntroPrice?.unit_amount_decimal,
  )
  const localizePrice = (price: number) =>
    localizeCurrency({ amountInCents: price, currency: priceCurrency })

  const planLabel =
    plan === "weekly"
      ? t("pages.checkout.plans.planLabelWeekly")
      : t("pages.checkout.plans.planLabelYearly")

  return (
    <Dialog onClose={onClose} open={open}>
      <div className="space-y-6 text-center">
        <h2 className="text-2xl font-bold">
          {t("pages.checkout.plans.dialog.limitedTimeOffer")}
        </h2>

        <p className="text-xl font-bold uppercase tracking-wide">{planLabel}</p>

        <div className="space-y-2">
          <p className="text-2xl text-gray-400 line-through">
            {localizePrice(price)}
          </p>

          <p className="text-5xl font-bold">{localizePrice(introPrice)}</p>
        </div>

        <SubscriptionForm
          buttonClassName="custom-button-primary uppercase"
          buttonTitle={t("pages.checkout.plans.continue")}
          currency={priceCurrency}
          fullPrice={stripePrice}
          isSubmitting={isSubmitting}
          plan={plan}
          priceId={stripeIntroPrice.id}
          setIsSubmitting={setIsSubmitting}
        />

        <div className="space-y-2">
          <p className="font-bold">
            {t("pages.checkout.plans.moneyBackGuarantee")}
          </p>

          <p className="text-sm">{t("pages.checkout.plans.cancelAnytime")}</p>
        </div>
      </div>
    </Dialog>
  )
}
